import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Camera, PencilSimple, At, User, TextAlignLeft, CheckCircle, WarningCircle, MapPin, GlobeHemisphereWest } from "@phosphor-icons/react";
import { accountApi, uploadsApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * EditProfileModal
 *
 * Props:
 *   open        — boolean to show/hide
 *   profile     — the current profile object (from Profile.jsx state)
 *   onClose     — called when modal dismisses
 *   onSaved     — called with updated profile data after successful save
 */
export default function EditProfileModal({ open, profile, onClose, onSaved }) {
    const { setUser } = useAuth();
    const fileInputRef = useRef(null);

    const [name, setName] = useState("");
    const [handle, setHandle] = useState("");
    const [bio, setBio] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [avatarUrl, setAvatarUrl] = useState("");
    const [avatarPreview, setAvatarPreview] = useState("");
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [saving, setSaving] = useState(false);

    // Reset form whenever the modal opens
    useEffect(() => {
        if (open && profile) {
            setName(profile.name || "");
            setHandle(profile.handle || "");
            setBio(profile.bio || "");
            const parsedCity = profile.city || (profile.location?.includes(",") ? profile.location.split(",")[0].trim() : (profile.location || ""));
            const parsedCountry = profile.country || (profile.location?.includes(",") ? profile.location.split(",")[1].trim() : "");
            setCity(parsedCity);
            setCountry(parsedCountry);
            setAvatarUrl(profile.avatar_url || "");
            setAvatarPreview(profile.avatar_url || "");
        }
    }, [open, profile]);

    const handleAvatarFile = useCallback(async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please select an image file.");
            return;
        }
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Image must be under 6MB.");
            return;
        }
        // Show instant local preview
        const localUrl = URL.createObjectURL(file);
        setAvatarPreview(localUrl);
        setUploadingAvatar(true);
        try {
            const uploadedUrl = await uploadsApi.upload(file);
            setAvatarUrl(uploadedUrl);
            setAvatarPreview(uploadedUrl);
        } catch {
            toast.error("Avatar upload failed. Please try again.");
            setAvatarPreview(avatarUrl); // revert to last good url
        } finally {
            setUploadingAvatar(false);
        }
    }, [avatarUrl]);

    const handleSave = async () => {
        if (!name.trim()) return toast.error("Display name cannot be empty.");
        if (name.trim().length > 50) return toast.error("Display name must be 50 characters or fewer.");
        const cleanHandle = handle.trim().toLowerCase().replace(/[^a-z0-9_.]/g, "");
        if (cleanHandle.length < 3 || cleanHandle.length > 20) {
            return toast.error("Username must be 3-20 characters (letters, numbers, dots, underscores).");
        }
        if (bio.length > 250) return toast.error("Bio must be 250 characters or fewer.");

        setSaving(true);
        try {
            const updated = await accountApi.updateProfile({
                name: name.trim(),
                handle: cleanHandle,
                bio: bio.trim(),
                city: city.trim(),
                country: country.trim(),
                avatar_url: avatarUrl,
            });
            // Sync auth context so header/avatars update instantly across the app
            setUser((prev) => prev ? { ...prev, ...updated } : prev);
            toast.success("Profile updated!");
            onSaved?.(updated);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not save profile. Please try again.";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    // Validate handle in real-time
    const handleValid = /^[a-z0-9_.]{3,20}$/.test(handle.trim().toLowerCase());
    const handleDirty = handle !== (profile?.handle || "");

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {/* Backdrop */}
                    <motion.div
                        className="absolute inset-0"
                        style={{ background: "rgba(7,10,17,0.85)", backdropFilter: "blur(12px)" }}
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <motion.div
                        className="relative w-full max-w-md rounded-3xl overflow-hidden"
                        style={{
                            background: "linear-gradient(155deg, rgba(212,175,55,0.07) 0%, rgba(11,19,32,0.98) 50%)",
                            border: "1px solid rgba(212,175,55,0.25)",
                            boxShadow: "0 0 60px rgba(212,175,55,0.12), 0 30px 80px rgba(0,0,0,0.6)",
                        }}
                        initial={{ scale: 0.93, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.93, y: 20, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                        {/* Header */}
                        <div
                            className="flex items-center justify-between px-6 py-5"
                            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-8 h-8 rounded-full grid place-items-center"
                                    style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)" }}
                                >
                                    <PencilSimple size={15} color="#D4AF37" />
                                </div>
                                <div>
                                    <div className="overline" style={{ fontSize: "9px" }}>PROFILE</div>
                                    <div className="font-serif text-lg text-white leading-tight">Edit Profile</div>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-white/10"
                                data-testid="edit-profile-close"
                            >
                                <X size={16} color="rgba(255,255,255,0.6)" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                            {/* Avatar */}
                            <div className="flex flex-col items-center gap-3">
                                <div className="relative group" style={{ width: 96, height: 96 }}>
                                    <img
                                        src={avatarPreview || `https://api.dicebear.com/7.x/notionists/svg?seed=${handle}`}
                                        alt="Avatar"
                                        className="w-24 h-24 rounded-full object-cover"
                                        style={{ border: "2px solid var(--gold)" }}
                                    />
                                    {/* Upload overlay */}
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingAvatar}
                                        className="absolute inset-0 rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ background: "rgba(7,10,17,0.7)" }}
                                        data-testid="avatar-upload-btn"
                                    >
                                        {uploadingAvatar ? (
                                            <div className="w-5 h-5 border-2 border-[color:var(--gold)] border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <Camera size={22} color="#D4AF37" />
                                        )}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => handleAvatarFile(e.target.files?.[0])}
                                    />
                                </div>
                                <p className="text-xs text-white/40">Tap to upload a new photo</p>
                            </div>

                            {/* Display Name */}
                            <div>
                                <label className="overline text-[9px] mb-2 block">DISPLAY NAME</label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                        <User size={14} color="rgba(255,255,255,0.35)" />
                                    </span>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        maxLength={50}
                                        placeholder="Your display name"
                                        className="w-full pl-9 pr-4 py-3 rounded-xl text-sm text-white focus:outline-none transition-colors"
                                        style={{
                                            background: "rgba(255,255,255,0.05)",
                                            border: `1px solid ${name.trim().length > 0 ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.1)"}`,
                                        }}
                                        data-testid="edit-profile-name"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-white/30">
                                        {name.length}/50
                                    </span>
                                </div>
                            </div>

                            {/* Handle */}
                            <div>
                                <label className="overline text-[9px] mb-2 block">USERNAME</label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35 text-sm select-none pointer-events-none">@</span>
                                    <input
                                        type="text"
                                        value={handle}
                                        onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ""))}
                                        maxLength={20}
                                        placeholder="yourhandle"
                                        className="w-full pl-8 pr-10 py-3 rounded-xl text-sm text-white focus:outline-none transition-colors"
                                        style={{
                                            background: "rgba(255,255,255,0.05)",
                                            border: `1px solid ${
                                                handleDirty
                                                    ? handleValid
                                                        ? "rgba(34,197,94,0.5)"
                                                        : "rgba(239,68,68,0.5)"
                                                    : "rgba(255,255,255,0.1)"
                                            }`,
                                        }}
                                        data-testid="edit-profile-handle"
                                    />
                                    {handleDirty && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2">
                                            {handleValid
                                                ? <CheckCircle size={16} color="#22c55e" />
                                                : <WarningCircle size={16} color="#ef4444" />
                                            }
                                        </span>
                                    )}
                                </div>
                                {handle.length >= 3 && (
                                    <p className="text-[10px] mt-1.5" style={{ color: "rgba(212,175,55,0.6)" }}>
                                        passport.com/profile/{handle}
                                    </p>
                                )}
                            </div>

                            {/* City & Country */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="overline text-[9px] mb-2 block">CITY</label>
                                    <div className="relative">
                                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <MapPin size={14} color="rgba(255,255,255,0.35)" />
                                        </span>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            maxLength={50}
                                            placeholder="e.g. Paris"
                                            className="w-full pl-9 pr-4 py-3 rounded-xl text-sm text-white focus:outline-none transition-colors"
                                            style={{
                                                background: "rgba(255,255,255,0.05)",
                                                border: `1px solid ${city.trim().length > 0 ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.1)"}`,
                                            }}
                                            data-testid="edit-profile-city"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="overline text-[9px] mb-2 block">COUNTRY</label>
                                    <div className="relative">
                                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <GlobeHemisphereWest size={14} color="rgba(255,255,255,0.35)" />
                                        </span>
                                        <input
                                            type="text"
                                            value={country}
                                            onChange={(e) => setCountry(e.target.value)}
                                            maxLength={50}
                                            placeholder="e.g. France"
                                            className="w-full pl-9 pr-4 py-3 rounded-xl text-sm text-white focus:outline-none transition-colors"
                                            style={{
                                                background: "rgba(255,255,255,0.05)",
                                                border: `1px solid ${country.trim().length > 0 ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.1)"}`,
                                            }}
                                            data-testid="edit-profile-country"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bio */}
                            <div>
                                <label className="overline text-[9px] mb-2 block">BIO</label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-3 pointer-events-none">
                                        <TextAlignLeft size={14} color="rgba(255,255,255,0.35)" />
                                    </span>
                                    <textarea
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        maxLength={250}
                                        rows={3}
                                        placeholder="Tell the world about your travels..."
                                        className="w-full pl-9 pr-4 pt-3 pb-8 rounded-xl text-sm text-white focus:outline-none transition-colors resize-none"
                                        style={{
                                            background: "rgba(255,255,255,0.05)",
                                            border: `1px solid ${bio.length > 0 ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.1)"}`,
                                        }}
                                        data-testid="edit-profile-bio"
                                    />
                                    <span
                                        className="absolute right-3 bottom-2.5 text-[10px]"
                                        style={{ color: bio.length > 230 ? "#ef4444" : "rgba(255,255,255,0.3)" }}
                                    >
                                        {bio.length}/250
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div
                            className="flex gap-3 px-6 py-5"
                            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                        >
                            <button
                                onClick={onClose}
                                className="flex-1 btn-ghost text-sm !py-3"
                                disabled={saving}
                                data-testid="edit-profile-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="flex-1 btn-gold text-sm !py-3"
                                disabled={saving || uploadingAvatar || !handleValid}
                                data-testid="edit-profile-save"
                            >
                                {saving ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="w-4 h-4 border-2 border-[color:var(--navy)] border-t-transparent rounded-full animate-spin" />
                                        Saving...
                                    </span>
                                ) : "Save Changes"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
